import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Link } from 'react-router-dom';

import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import AddBoxIcon from '@material-ui/icons/AddBox';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import makeStyles from '@material-ui/core/styles/makeStyles';

import SidebarMenuExpansionPanel from './components/SidebarMenuExpansionPanel';
import LoadingSpinner from 'components/LoadingSpinner';

const useStyles = makeStyles(theme => ({
  flexed: {
    display: 'flex',
    alignItems: 'center'
  },
  title: {
    fontSize: '24px',
    lineHeight: '29px',
    letterSpacing: '0px',
    color: theme.palette.primary.main,
    textTransform: 'uppercase',
    fontWeight: theme.typography.fontWeightBold,
    height: '66px',
    padding: '0 16px'
  },
  link: {
    fontSize: '12px',
    lineHeight: '41px',
    marginTop: '6px',
    textTransform: 'none',
    height: '40px',
    textDecoration: 'none',
    cursor: 'pointer',
    padding: '0 16px'
  },
  dashboardLink: {
    marginTop: 0,
    padding: '24px 0 24px 16px'
  },
  dashboardLinkTitle: {
    fontSize: '13px',
    fontWeight: theme.typography.fontWeightBold,
    whiteSpace: 'nowrap'
  },
  dashboardCreateNewLink: {
    padding: '0 16px 0 9.5px'
  },
  dashboardCreateNewTypo: {
    fontSize: '13px',
    lineHeight: '41px',
    color: theme.palette.primary.main,
    cursor: 'pointer'
  },
  dashboardCreateNewIcon: {
    color: theme.palette.primary.main,
    fontSize: '37px',
    marginRight: '8px'
  },
  cannedDashboardLink: {
    color: theme.palette.white,
    textDecoration: 'none',
    cursor: 'pointer'
  },
  cannedDashboardTypo: {
    fontSize: '14px',
    lineHeight: '36px',
    letterSpacing: 0,
    cursor: 'pointer',
    marginLeft: '3%'
  },
  portfolioViewAllTypo: {
    fontSize: '13px',
    lineHeight: '41px',
    color: theme.palette.primary.main
  },
  portfolioViewAllIcon: {
    color: theme.palette.white,
    fontSize: '24px',
    cursor: 'pointer'
  },
  divider: {
    opacity: '0.26'
  },
  blueDivider: {
    height: '2px',
    backgroundColor: theme.palette.lightBlue.main
  },
  blueDividerWithMargin: {
    margin: '0 16px',
    backgroundColor: theme.palette.lightBlue.main
  },
  whiteDivider: {
    backgroundColor: theme.palette.white
  },
  arrowIcon: {
    fontSize: '14px',
    color: theme.palette.white,
    marginRight: '10px',
    cursor: 'pointer'
  }
}));

const dashboardCreateLocation = {
  pathname: '/dashboard',
  state: { isDashboardCreation: true }
};

const SidebarMenu = ({ title, menuItems }) => {
  const [expanded, setExpanded] = useState();

  const classes = useStyles();

  const handleExpandedChange = event => {
    if (expanded === event.currentTarget.id) {
      setExpanded();
      return;
    }
    setExpanded(event.currentTarget.id);
  };

  const getMenuItemRoute  = (item) => {
    return item.masterObjectId ? `/canned/${item.masterObjectId}` :
      item.dashboardId ? `/dashboard/${item.dashboardId}` :
      item.to;
  }

  const truncateRecentDashboardText = (str, masterObjectType=null, maxLength) => {
    const truncatedStr = masterObjectType ? `${masterObjectType}: ${str}` : str;
    return truncatedStr.length > maxLength ? `${truncatedStr.slice(0, maxLength)}...` : truncatedStr;
  }

  return (
    <>
      <Typography variant="h3" className={classNames(classes.flexed, classes.title)}>
        {title}
      </Typography>
      <Divider className={classNames(classes.divider, classes.blueDivider)} />
      {menuItems ?
        menuItems.map(item => (
          <React.Fragment key={item.id}>
            {!item.dashboards && (
              <>
                <Link
                  to={getMenuItemRoute (item)}
                  className={classNames(classes.flexed, classes.link, classes.dashboardLink)}
                  style={{
                    color: expanded === item.id ? '#fff' : '#346C9F',
                    backgroundColor: expanded === item.id && '#34BEB0'
                  }}
                >
                  <Typography className={classes.dashboardLinkTitle}>
                    {item.masterObjectId || item.dashboardId ? (
                      <span title={item.name}>
                        {truncateRecentDashboardText(item.name, item.masterObjectType,  30)}
                      </span>
                    ) : (
                      <span title={item.title}>
                        {truncateRecentDashboardText(item.title, item.masterObjectType, 30)}
                      </span>
                    )}
                  </Typography>
                </Link>
                <Divider className={classNames(classes.divider, classes.blueDividerWithMargin)} />
              </>
            )}
            {item.dashboards && (
              <>
                <SidebarMenuExpansionPanel
                  title={item.name}
                  id={`${item.id}`}
                  expanded={expanded}
                  handleChange={handleExpandedChange}
                >
                  <Box bgcolor="#34BEB0" px={2} color="#fff">
                    <Divider className={classNames(classes.divider, classes.whiteDivider)} />

                    {item.dashboards &&
                      item.dashboards.map((child, index) => (
                        <React.Fragment key={child.name}>
                          <Link
                            to={`/canned/${item.id}/${child.id}`}
                            className={classes.cannedDashboardLink}
                          >
                            <Typography
                              className={classNames(classes.flexed, classes.cannedDashboardTypo)}
                            >
                              <ArrowForwardIcon className={classes.arrowIcon} /> {child.name}
                            </Typography>
                          </Link>
                          {item.dashboards.length - 1 > index && (
                            <Divider
                              className={classNames(classes.divider, classes.whiteDivider)}
                            />
                          )}
                        </React.Fragment>
                      ))}
                  </Box>
                </SidebarMenuExpansionPanel>
                <Divider className={classNames(classes.divider, classes.blueDividerWithMargin)} />
              </>
            )}
          </React.Fragment>
        )): <LoadingSpinner /> }
      {title === 'Dashboard' && (
        <Link
          to={dashboardCreateLocation}
          className={classNames(classes.flexed, classes.link, classes.dashboardCreateNewLink)}
        >
          <Typography className={classNames(classes.flexed, classes.dashboardCreateNewTypo)}>
            <AddBoxIcon className={classes.dashboardCreateNewIcon} />
            Create new
          </Typography>
        </Link>
      )}

      {title === 'Portfolios' && (
        <Link to="/portfolios" className={classNames(classes.flexed, classes.link)}>
          <Typography className={classNames(classes.flexed, classes.portfolioViewAllTypo)}>
            <Box
              component="span"
              bgcolor="#346C9F"
              height="28px"
              width="28px"
              mr={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <VisibilityOutlinedIcon className={classes.portfolioViewAllIcon} />
            </Box>
            View All Portfolios
          </Typography>
        </Link>
      )}

      {title === 'Holdings' && (
        <Link to="/holdings" className={classNames(classes.flexed, classes.link)}>
          <Typography className={classNames(classes.flexed, classes.portfolioViewAllTypo)}>
            <Box
              component="span"
              bgcolor="#346C9F"
              height="28px"
              width="28px"
              mr={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <VisibilityOutlinedIcon className={classes.portfolioViewAllIcon} />
            </Box>
            View All Holdings
          </Typography>
        </Link>
      )}
      {title === 'Instruments' && (
        <Link to="/instruments" className={classNames(classes.flexed, classes.link)}>
          <Typography className={classNames(classes.flexed, classes.portfolioViewAllTypo)}>
            <Box
              component="span"
              bgcolor="#346C9F"
              height="28px"
              width="28px"
              mr={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <VisibilityOutlinedIcon className={classes.portfolioViewAllIcon} />
            </Box>
            View All Instruments
          </Typography>
        </Link>
      )}
      {title === 'Peer Groups' && (
        <Link to="/peer-groups" className={classNames(classes.flexed, classes.link)}>
          <Typography className={classNames(classes.flexed, classes.portfolioViewAllTypo)}>
            <Box
              component="span"
              bgcolor="#346C9F"
              height="28px"
              width="28px"
              mr={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <VisibilityOutlinedIcon className={classes.portfolioViewAllIcon} />
            </Box>
            View All Peer Groups
          </Typography>
        </Link>
      )}
    </>
  );
};

SidebarMenu.propTypes = {
  title: PropTypes.string.isRequired,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      typeId: PropTypes.number,
      dashboards: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string
        })
      )
    })
  )
};

SidebarMenu.defaultProps = {
  menuItems: null
};

export default SidebarMenu;
